import React, { useState, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { number, string, shape, oneOfType, func } from 'prop-types'
import { useForm } from 'react-hook-form'
import CurrencyFormat from 'react-currency-format'
import { Button } from '@gympass/yoga'
import { Close } from '@gympass/yoga-icons'
import tokens from '@gympass/yoga-tokens'

import useLoading from '@hooks/useLoading'
import useCoupon from '@hooks/useCoupon'
import useInternationalization from '@hooks/useInternationalization'

import LoadingButton from '@components/LoadingButton'

import * as Style from './OfferCard.style'

export default function Card({ step, path, actions, onNext, contact }) {
  const { setDataLayer } = actions

  const { handleSubmit, register } = useForm()
  const { handleCoupon, isCouponValid, hasCoupon } = useCoupon()
  const { isLoading, startLoad, finishLoad } = useLoading()

  const [couponForm, setCouponForm] = useState({
    isOpened: false,
  })

  const {
    formats: { currency },
  } = useInternationalization()

  function openCouponField() {
    setCouponForm({ isOpened: true })
  }

  function closeCouponField() {
    setCouponForm({ isOpened: false })
  }

  async function handleCouponForm({ coupon }) {
    await handleCoupon(contact.employeesNumber, coupon)
  }

  async function handleContract() {
    startLoad()

    setDataLayer(step, path, {
      actorData: {
        email: contact.email,
        role: contact.role,
        department: 'Human Resources',
      },
      companyData: {
        employeesNumber: contact.employeesNumber,
        industry: contact.industry,
        state: contact.state,
      },
      transactionData: {
        offerValue: contact.proposalValue,
      },
    })

    finishLoad()

    if (onNext) {
      onNext()
    }
  }

  useEffect(() => {
    if (isCouponValid) {
      closeCouponField()
    }
  }, [isCouponValid])

  return (
    <Style.Card>
      <Style.CardHeader>
        <Style.CardTitle>
          <FormattedMessage id="smb_acquisition_checkout_summary.title.summary" />
        </Style.CardTitle>
      </Style.CardHeader>

      <Style.CardContent>
        <Style.PriceRow>
          <Style.PriceDescription>
            <FormattedMessage
              id="smb_acquisition_checkout_summary.description.benefit_value"
              values={{
                numberOfEmployees: <strong>{contact.employeesNumber}</strong>,
              }}
            />
          </Style.PriceDescription>
          <Style.PriceValue>
            <CurrencyFormat
              prefix={currency.moneySign}
              value={contact.proposalValueWithoutDiscount}
              displayType="text"
              thousandSeparator={currency.thousandSeparator}
              decimalSeparator={currency.decimalSeparator}
              decimalScale={2}
              fixedDecimalScale
            />
          </Style.PriceValue>
        </Style.PriceRow>

        {contact.discount < 0 && (
          <Style.PriceRow>
            <Style.PriceDescription>
              <FormattedMessage id="smb_acquisition_checkout_summary.description.discount_coupon" />
            </Style.PriceDescription>
            <Style.PriceValue isDiscount>
              <CurrencyFormat
                prefix={currency.moneySign}
                value={contact.discount}
                displayType="text"
                thousandSeparator={currency.thousandSeparator}
                decimalSeparator={currency.decimalSeparator}
                decimalScale={2}
                fixedDecimalScale
              />
            </Style.PriceValue>
          </Style.PriceRow>
        )}

        <Style.Divider />

        {contact.discount < 0 && (
          <Style.PriceRow>
            <Style.PriceDescription>
              <FormattedMessage id="smb_acquisition_checkout_summary.description.total_value" />
            </Style.PriceDescription>
            <Style.PriceValue>
              <Style.StrippedValue>
                <CurrencyFormat
                  prefix={currency.moneySign}
                  value={contact.proposalValueWithoutDiscount}
                  displayType="text"
                  thousandSeparator={currency.thousandSeparator}
                  decimalSeparator={currency.decimalSeparator}
                  decimalScale={2}
                  fixedDecimalScale
                />
                /
                <FormattedMessage id="smb_acquisition_commons.frequency.monthly" />
              </Style.StrippedValue>
            </Style.PriceValue>
          </Style.PriceRow>
        )}

        <Style.PriceRow>
          <Style.PriceDescription>
            <span>
              {contact.discount < 0 && (
                <FormattedMessage id="smb_acquisition_checkout_summary.description.total_with_discount" />
              )}
              {!contact.discount && (
                <FormattedMessage id="smb_acquisition_checkout_summary.description.total_value" />
              )}
            </span>
          </Style.PriceDescription>
          <Style.PriceValue>
            <Style.TotalWrapper>
              <em>{currency.moneySign}</em>
              <CurrencyFormat
                value={contact.proposalValue}
                displayType="text"
                thousandSeparator={currency.thousandSeparator}
                decimalSeparator={currency.decimalSeparator}
                decimalScale={2}
                fixedDecimalScale
              />
              /
              <FormattedMessage id="smb_acquisition_commons.frequency.monthly" />
            </Style.TotalWrapper>
          </Style.PriceValue>
        </Style.PriceRow>
      </Style.CardContent>

      <Style.CardActions>
        {!couponForm.isOpened ? (
          <>
            <LoadingButton loading={isLoading} full onClick={handleContract}>
              <Style.ButtonText>
                <FormattedMessage id="smb_acquisition_checkout_summary.call_to_action.default" />
              </Style.ButtonText>
            </LoadingButton>
            <Style.LinkButton onClick={openCouponField}>
              <Style.ButtonText>
                <FormattedMessage id="smb_acquisition_discount_coupon.call_to_action.open" />
              </Style.ButtonText>
            </Style.LinkButton>
          </>
        ) : (
          <Style.CouponWrapper>
            <Style.Form>
              <Style.Input name="coupon" ref={register} />
              <Button onClick={handleSubmit(handleCouponForm)}>
                <Style.ButtonText>
                  <FormattedMessage id="smb_acquisition_discount_coupon.call_to_action.apply" />
                </Style.ButtonText>
              </Button>
            </Style.Form>

            <Style.CouponRow>
              {hasCoupon && !isCouponValid && (
                <Style.ErrorMessage>
                  <FormattedMessage id="smb_acquisition_discount_coupon.messages.not_valid" />
                </Style.ErrorMessage>
              )}
              <Style.CancelCoupon onClick={closeCouponField}>
                <Close stroke={tokens.colors.vibin} width={4} height={4} />

                <FormattedMessage id="smb_acquisition_discount_coupon.call_to_action.cancel" />
              </Style.CancelCoupon>
            </Style.CouponRow>
          </Style.CouponWrapper>
        )}
      </Style.CardActions>
    </Style.Card>
  )
}

Card.propTypes = {
  contact: shape({
    employeesNumber: oneOfType([number, string]),
  }).isRequired,
  path: string.isRequired,
  actions: shape({
    setFunnelStage: func,
    setDataLayer: func,
  }).isRequired,
  step: number.isRequired,
  onNext: func,
}

Card.defaultProps = {
  onNext: undefined,
}
